import { Content } from "@vivport/backend/types";

/** Server sentinel for fields not yet produced by generation (matches backend). */
export const NZ_TO_GENERATE_SENTINEL = "<NZ_TO_GENERATE>";

/** True when the value is empty or still the not-yet-generated placeholder. */
export function isUnsetOrNzPlaceholder(value: string | null | undefined): boolean {
  if (value == null) {
    return true;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 || trimmed === NZ_TO_GENERATE_SENTINEL;
}
// ———————————————————————————————————————————————————————————————
// Progress derivation per content type
// ———————————————————————————————————————————————————————————————
/**
 * Derives progress information for a content item based on its type and state
 */
/** Marker prefix for AsyncQueue jobs created from content detail "Regenerate" (description). */
export const CONTENT_DESCRIPTION_REGEN_TASK_PREFIX = 'regen-desc';

export function deriveProgress(
  item: Content, 
  marker?: string
): { progress: number; status: 'pending' | 'processing' | 'completed' | 'failed'; description: string } {
  let progress = 0;
  let status: 'pending' | 'processing' | 'completed' | 'failed' = 'processing';
  let description = 'Processing...';

  if (marker?.startsWith(CONTENT_DESCRIPTION_REGEN_TASK_PREFIX)) {
    return {
      progress: 55,
      status: 'processing',
      description: `Regenerating ${item.contentType.toLowerCase()}…`,
    };
  }
  
  const done = () => { 
    progress = 100; 
    status = 'completed'; 
    description = 'Processing complete'; 
  };
  const looksLikeScaffold = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'generated title' ||
      normalized.startsWith('generated description based on')
    );
  };
  const isGeneratedValueReady = (value: string | null | undefined): boolean =>
    Boolean(
      value &&
      value !== NZ_TO_GENERATE_SENTINEL &&
      value.trim().length > 0 &&
      !looksLikeScaffold(value)
    );
  const hasReadyDocumentText = (value: unknown): boolean =>
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value !== NZ_TO_GENERATE_SENTINEL;

  switch (item.contentType) {
    case 'PHOTO':
      // For photos, both title and description need to be generated
      const hasPhotoDescription = isGeneratedValueReady(item.description);
      const hasPhotoTitle = isGeneratedValueReady(item.title);
      (hasPhotoTitle && hasPhotoDescription)
        ? done()
        : (progress = 50, description = 'Processing photo...');
      break;
    case 'RECORDING':
      // For recordings, check if we have both transcript and the AI-generated description
      const recording = item as Extract<Content, { contentType: 'RECORDING' }>;
      const hasRecordingDesc = isGeneratedValueReady(item.description);
      (recording.transcript && hasRecordingDesc)
        ? done()
        : (progress = 50, description = 'Transcribing audio...');
      break;
    case 'VIDEO':
      // A video is stored as-is and never generated for, so it is complete on arrival.
      done();
      break;
    case 'ANCHOR':
    case 'EVENT':
      // For all content types, both title and description need to be generated
      const hasDesc = isGeneratedValueReady(item.description);
      const hasTitle = isGeneratedValueReady(item.title);
      (hasTitle && hasDesc)
        ? done()
        : (progress = 50, description = 'Regenerating description...');
      break;
    case 'DOCUMENT':
      // Notes-first documents are valid once body text exists, even if generated metadata is still pending.
      const hasDocumentDesc = isGeneratedValueReady(item.description);
      const hasDocumentTitle = isGeneratedValueReady(item.title);
      const hasDocumentBody = hasReadyDocumentText((item as { text?: unknown }).text);
      (hasDocumentBody || (hasDocumentTitle && hasDocumentDesc))
        ? done()
        : (progress = 50, description = 'Finalizing document metadata...');
      break;
  }
  
  if (marker?.includes('regenerate') && status === 'processing') {
    description = `Regenerating ${item.contentType.toLowerCase()}...`;
  }
  
  return { progress, status, description };
}
